/**
 * useAdminAffairs — Admin-Web hooks for Issue #365.
 *
 * 红线 (AI GUIDE §6 / §7):
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch、不构造假状态。
 *   - DTO 仅承载 id 字段；姓名/手机号/身份证不进入前端 hook 类型。
 *   - 错误统一文案：409 提示乐观锁冲突或时间冲突，403 提示无权限，404 提示
 *     记录不存在。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type AdminMeetingRoomDto =
  ApiComponents['schemas']['AdminMeetingRoomDto'];
export type AdminMeetingRoomListDto =
  ApiComponents['schemas']['AdminMeetingRoomListDto'];
export type AdminMeetingBookingDto =
  ApiComponents['schemas']['AdminMeetingBookingDto'];
export type AdminMeetingBookingListDto =
  ApiComponents['schemas']['AdminMeetingBookingListDto'];
export type AdminVehicleRequestDto =
  ApiComponents['schemas']['AdminVehicleRequestDto'];
export type AdminVehicleRequestListDto =
  ApiComponents['schemas']['AdminVehicleRequestListDto'];
export type AdminSealRequestDto =
  ApiComponents['schemas']['AdminSealRequestDto'];
export type AdminSealRequestListDto =
  ApiComponents['schemas']['AdminSealRequestListDto'];
export type AdminAnnouncementDto =
  ApiComponents['schemas']['AdminAnnouncementDto'];
export type AdminAnnouncementListDto =
  ApiComponents['schemas']['AdminAnnouncementListDto'];
export type AdminAnnouncementReceiptDto =
  ApiComponents['schemas']['AdminAnnouncementReceiptDto'];
export type AdminAnnouncementReceiptListDto =
  ApiComponents['schemas']['AdminAnnouncementReceiptListDto'];
export type AdminAffairEventDto =
  ApiComponents['schemas']['AdminAffairEventDto'];
export type AdminAffairEventListDto =
  ApiComponents['schemas']['AdminAffairEventListDto'];
export type CreateAdminMeetingRoomDto =
  ApiComponents['schemas']['CreateAdminMeetingRoomDto'];
export type CreateAdminMeetingBookingDto =
  ApiComponents['schemas']['CreateAdminMeetingBookingDto'];
export type CreateAdminVehicleRequestDto =
  ApiComponents['schemas']['CreateAdminVehicleRequestDto'];
export type CreateAdminSealRequestDto =
  ApiComponents['schemas']['CreateAdminSealRequestDto'];
export type CreateAdminAnnouncementDto =
  ApiComponents['schemas']['CreateAdminAnnouncementDto'];

export const ADMIN_MEETING_BOOKING_STATUS_LABELS: Readonly<
  Record<AdminMeetingBookingDto['status'], string>
> = {
  pending: '待审批',
  confirmed: '已确认',
  cancelled: '已取消',
  rejected: '已拒绝',
  completed: '已完成',
};

export const ADMIN_AFFAIR_REQUEST_STATUS_LABELS: Readonly<
  Record<AdminVehicleRequestDto['status'], string>
> = {
  draft: '草稿',
  submitted: '审批中',
  approved: '已审批',
  rejected: '已拒绝',
  completed: '已完成',
  cancelled: '已取消',
};

export const ADMIN_ANNOUNCEMENT_STATUS_LABELS: Readonly<
  Record<AdminAnnouncementDto['status'], string>
> = {
  draft: '草稿',
  published: '已发布',
  retracted: '已撤回',
  archived: '已归档',
};

export const ADMIN_ANNOUNCEMENT_SCOPE_LABELS: Readonly<
  Record<AdminAnnouncementDto['scopeType'], string>
> = {
  all: '全员',
  org: '整个组织',
  department: '指定部门',
  role: '指定角色',
  user: '指定用户',
};

export const ADMIN_SEAL_TYPE_LABELS: Readonly<
  Record<AdminSealRequestDto['sealType'], string>
> = {
  company: '公章',
  contract: '合同章',
  finance: '财务章',
  legal: '法人章',
  hr: '人事章',
  other: '其他',
};

export const ADMIN_MEETING_ROOM_STATUS_LABELS: Readonly<
  Record<AdminMeetingRoomDto['status'], string>
> = {
  active: '可用',
  inactive: '已停用',
  archived: '已归档',
};

export interface AdminAffairsApiError {
  readonly message: string;
  readonly status?: number;
}

function toApiError(err: unknown, status?: number): AdminAffairsApiError {
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或时间窗冲突，请刷新后重试',
      status,
    };
  }
  if (status === 403) return { message: '没有权限执行此操作', status };
  if (status === 400) {
    return { message: '请求参数不合法，请检查必填项与状态机限制', status };
  }
  if (status === 404) return { message: '记录不存在或不可访问', status };
  if (err instanceof Error) return { message: err.message, status };
  return { message: String(err), status };
}

// ---------------------------------------------------------------------------
// Generic wrapper
// ---------------------------------------------------------------------------

interface ApiResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

function useWrap() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminAffairsApiError | null>(null);
  const wrap = useCallback(
    async <T>(fn: () => Promise<ApiResult<T>>): Promise<T | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await fn();
        if (apiErr) {
          setError(toApiError(apiErr, response?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { loading, error, setError, wrap };
}

// ---------------------------------------------------------------------------
// Meeting rooms
// ---------------------------------------------------------------------------

export function useAdminMeetingRooms() {
  const [items, setItems] = useState<AdminMeetingRoomDto[]>([]);
  const [total, setTotal] = useState(0);
  const { loading, error, wrap } = useWrap();
  const fetchRooms = useCallback(
    async (filter?: {
      search?: string;
      status?: AdminMeetingRoomDto['status'];
      includeArchived?: boolean;
      page?: number;
      limit?: number;
    }) => {
      const data = await wrap<AdminMeetingRoomListDto>(() =>
        apiClient.GET('/api/v1/admin/affairs/meeting-rooms', {
          params: { query: filter ?? {} },
        }) as unknown as Promise<ApiResult<AdminMeetingRoomListDto>>,
      );
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data;
    },
    [wrap],
  );
  return { items, total, loading, error, fetchRooms };
}

export function useAdminMeetingRoomMutations() {
  const { loading, error, wrap } = useWrap();
  const createRoom = useCallback(
    (body: CreateAdminMeetingRoomDto) =>
      wrap<AdminMeetingRoomDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/meeting-rooms', {
          body,
        }) as unknown as Promise<ApiResult<AdminMeetingRoomDto>>,
      ),
    [wrap],
  );
  const archiveRoom = useCallback(
    (id: string, expectedVersion: number, reason?: string) =>
      wrap<AdminMeetingRoomDto>(() =>
        apiClient.POST(
          '/api/v1/admin/affairs/meeting-rooms/{id}/archive',
          {
            params: { path: { id } },
            body: { expectedVersion, reason },
          },
        ) as unknown as Promise<ApiResult<AdminMeetingRoomDto>>,
      ),
    [wrap],
  );
  return { loading, error, createRoom, archiveRoom };
}

// ---------------------------------------------------------------------------
// Meeting bookings
// ---------------------------------------------------------------------------

export function useAdminMeetingBookings() {
  const [items, setItems] = useState<AdminMeetingBookingDto[]>([]);
  const [total, setTotal] = useState(0);
  const { loading, error, wrap } = useWrap();
  const fetchBookings = useCallback(
    async (filter?: {
      roomId?: string;
      applicantId?: string;
      status?: AdminMeetingBookingDto['status'];
      startFrom?: string;
      startTo?: string;
      page?: number;
      limit?: number;
    }) => {
      const data = await wrap<AdminMeetingBookingListDto>(() =>
        apiClient.GET('/api/v1/admin/affairs/bookings', {
          params: { query: filter ?? {} },
        }) as unknown as Promise<ApiResult<AdminMeetingBookingListDto>>,
      );
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data;
    },
    [wrap],
  );
  return { items, total, loading, error, fetchBookings };
}

export function useAdminMeetingBookingMutations() {
  const { loading, error, wrap } = useWrap();
  const createBooking = useCallback(
    (body: CreateAdminMeetingBookingDto) =>
      wrap<AdminMeetingBookingDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/bookings', {
          body,
        }) as unknown as Promise<ApiResult<AdminMeetingBookingDto>>,
      ),
    [wrap],
  );
  const confirmBooking = useCallback(
    (id: string, expectedVersion: number) =>
      wrap<AdminMeetingBookingDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/bookings/{id}/confirm', {
          params: { path: { id } },
          body: { expectedVersion },
        }) as unknown as Promise<ApiResult<AdminMeetingBookingDto>>,
      ),
    [wrap],
  );
  const rejectBooking = useCallback(
    (id: string, expectedVersion: number, reason: string) =>
      wrap<AdminMeetingBookingDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/bookings/{id}/reject', {
          params: { path: { id } },
          body: { expectedVersion, reason },
        }) as unknown as Promise<ApiResult<AdminMeetingBookingDto>>,
      ),
    [wrap],
  );
  const cancelBooking = useCallback(
    (id: string, expectedVersion: number, reason?: string) =>
      wrap<AdminMeetingBookingDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/bookings/{id}/cancel', {
          params: { path: { id } },
          body: { expectedVersion, reason },
        }) as unknown as Promise<ApiResult<AdminMeetingBookingDto>>,
      ),
    [wrap],
  );
  const completeBooking = useCallback(
    (id: string, expectedVersion: number) =>
      wrap<AdminMeetingBookingDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/bookings/{id}/complete', {
          params: { path: { id } },
          body: { expectedVersion },
        }) as unknown as Promise<ApiResult<AdminMeetingBookingDto>>,
      ),
    [wrap],
  );
  return {
    loading,
    error,
    createBooking,
    confirmBooking,
    rejectBooking,
    cancelBooking,
    completeBooking,
  };
}

// ---------------------------------------------------------------------------
// Vehicle requests
// ---------------------------------------------------------------------------

export function useAdminVehicleRequests() {
  const [items, setItems] = useState<AdminVehicleRequestDto[]>([]);
  const [total, setTotal] = useState(0);
  const { loading, error, wrap } = useWrap();
  const fetchVehicleRequests = useCallback(
    async (filter?: {
      applicantId?: string;
      status?: AdminVehicleRequestDto['status'];
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const data = await wrap<AdminVehicleRequestListDto>(() =>
        apiClient.GET('/api/v1/admin/affairs/vehicle-requests', {
          params: { query: filter ?? {} },
        }) as unknown as Promise<ApiResult<AdminVehicleRequestListDto>>,
      );
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data;
    },
    [wrap],
  );
  return { items, total, loading, error, fetchVehicleRequests };
}

export function useAdminVehicleRequestMutations() {
  const { loading, error, wrap } = useWrap();
  const create = useCallback(
    (body: CreateAdminVehicleRequestDto) =>
      wrap<AdminVehicleRequestDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/vehicle-requests', {
          body,
        }) as unknown as Promise<ApiResult<AdminVehicleRequestDto>>,
      ),
    [wrap],
  );
  const action = useCallback(
    async (
      id: string,
      kind: 'submit' | 'approve' | 'reject' | 'cancel' | 'complete',
      expectedVersion: number,
      reason?: string,
    ) => {
      const path =
        `/api/v1/admin/affairs/vehicle-requests/{id}/${kind}` as const;
      const body =
        kind === 'reject'
          ? { expectedVersion, reason: reason ?? '' }
          : kind === 'cancel'
            ? { expectedVersion, reason }
            : { expectedVersion };
      return wrap<AdminVehicleRequestDto>(() =>
        apiClient.POST(path, {
          params: { path: { id } },
          body,
        } as never) as unknown as Promise<ApiResult<AdminVehicleRequestDto>>,
      );
    },
    [wrap],
  );
  return { loading, error, create, action };
}

// ---------------------------------------------------------------------------
// Seal requests
// ---------------------------------------------------------------------------

export function useAdminSealRequests() {
  const [items, setItems] = useState<AdminSealRequestDto[]>([]);
  const [total, setTotal] = useState(0);
  const { loading, error, wrap } = useWrap();
  const fetchSealRequests = useCallback(
    async (filter?: {
      applicantId?: string;
      status?: AdminSealRequestDto['status'];
      sealType?: AdminSealRequestDto['sealType'];
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const data = await wrap<AdminSealRequestListDto>(() =>
        apiClient.GET('/api/v1/admin/affairs/seal-requests', {
          params: { query: filter ?? {} },
        }) as unknown as Promise<ApiResult<AdminSealRequestListDto>>,
      );
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data;
    },
    [wrap],
  );
  return { items, total, loading, error, fetchSealRequests };
}

export function useAdminSealRequestMutations() {
  const { loading, error, wrap } = useWrap();
  const create = useCallback(
    (body: CreateAdminSealRequestDto) =>
      wrap<AdminSealRequestDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/seal-requests', {
          body,
        }) as unknown as Promise<ApiResult<AdminSealRequestDto>>,
      ),
    [wrap],
  );
  const action = useCallback(
    async (
      id: string,
      kind: 'submit' | 'approve' | 'reject' | 'cancel' | 'complete',
      expectedVersion: number,
      reason?: string,
    ) => {
      const path =
        `/api/v1/admin/affairs/seal-requests/{id}/${kind}` as const;
      const body =
        kind === 'reject'
          ? { expectedVersion, reason: reason ?? '' }
          : kind === 'cancel'
            ? { expectedVersion, reason }
            : { expectedVersion };
      return wrap<AdminSealRequestDto>(() =>
        apiClient.POST(path, {
          params: { path: { id } },
          body,
        } as never) as unknown as Promise<ApiResult<AdminSealRequestDto>>,
      );
    },
    [wrap],
  );
  return { loading, error, create, action };
}

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

export function useAdminAnnouncements() {
  const [items, setItems] = useState<AdminAnnouncementDto[]>([]);
  const [total, setTotal] = useState(0);
  const { loading, error, wrap } = useWrap();
  const fetchAnnouncements = useCallback(
    async (filter?: {
      status?: AdminAnnouncementDto['status'];
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const data = await wrap<AdminAnnouncementListDto>(() =>
        apiClient.GET('/api/v1/admin/affairs/announcements', {
          params: { query: filter ?? {} },
        }) as unknown as Promise<ApiResult<AdminAnnouncementListDto>>,
      );
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data;
    },
    [wrap],
  );
  return { items, total, loading, error, fetchAnnouncements };
}

export function useAdminAnnouncementMutations() {
  const { loading, error, wrap } = useWrap();
  const create = useCallback(
    (body: CreateAdminAnnouncementDto) =>
      wrap<AdminAnnouncementDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/announcements', {
          body,
        }) as unknown as Promise<ApiResult<AdminAnnouncementDto>>,
      ),
    [wrap],
  );
  const publish = useCallback(
    (id: string, expectedVersion: number) =>
      wrap<AdminAnnouncementDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/announcements/{id}/publish', {
          params: { path: { id } },
          body: { expectedVersion },
        }) as unknown as Promise<ApiResult<AdminAnnouncementDto>>,
      ),
    [wrap],
  );
  const retract = useCallback(
    (id: string, expectedVersion: number, reason?: string) =>
      wrap<AdminAnnouncementDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/announcements/{id}/retract', {
          params: { path: { id } },
          body: { expectedVersion, reason },
        }) as unknown as Promise<ApiResult<AdminAnnouncementDto>>,
      ),
    [wrap],
  );
  const archive = useCallback(
    (id: string, expectedVersion: number, reason?: string) =>
      wrap<AdminAnnouncementDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/announcements/{id}/archive', {
          params: { path: { id } },
          body: { expectedVersion, reason },
        }) as unknown as Promise<ApiResult<AdminAnnouncementDto>>,
      ),
    [wrap],
  );
  const markRead = useCallback(
    (id: string, acknowledge: boolean) =>
      wrap<AdminAnnouncementReceiptDto>(() =>
        apiClient.POST('/api/v1/admin/affairs/announcements/{id}/read', {
          params: { path: { id } },
          body: { acknowledge },
        }) as unknown as Promise<ApiResult<AdminAnnouncementReceiptDto>>,
      ),
    [wrap],
  );
  return { loading, error, create, publish, retract, archive, markRead };
}

export function useAdminAnnouncementReceipts(announcementId?: string) {
  const [data, setData] = useState<AdminAnnouncementReceiptListDto | null>(null);
  const { loading, error, wrap } = useWrap();
  const fetchReceipts = useCallback(
    async (id?: string) => {
      const target = id ?? announcementId;
      if (!target) return null;
      const result = await wrap<AdminAnnouncementReceiptListDto>(() =>
        apiClient.GET(
          '/api/v1/admin/affairs/announcements/{id}/receipts',
          { params: { path: { id: target } } },
        ) as unknown as Promise<ApiResult<AdminAnnouncementReceiptListDto>>,
      );
      if (result) setData(result);
      return result;
    },
    [announcementId, wrap],
  );
  return { data, loading, error, fetchReceipts };
}
